
// SEO and URL utilities for Trade Relations Atlas

/**
 * Convert a string to a URL-safe slug
 * @param {string} text - The text to convert
 * @returns {string} - URL-safe slug
 */
export const createSlug = (text) => {
  if (!text) return '';
  
  return text
    .toString()
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[\s\W-]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Replace multiple consecutive hyphens with single hyphen
    .replace(/-+/g, '-');
};

/**
 * Convert a slug back to a readable title (for reverse lookup)
 * @param {string} slug - The slug to convert
 * @returns {string} - Readable title
 */
export const slugToTitle = (slug) => {
  if (!slug) return '';
  
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate country-specific slug with special handling for common cases
 * @param {string} countryName - The country name
 * @returns {string} - SEO-friendly country slug
 */
export const createCountrySlug = (countryName) => {
  if (!countryName) return '';
  
  // Handle special cases for better SEO
  const specialCases = {
    'United States': 'united-states',
    'United States of America': 'united-states',
    'USA': 'united-states',
    'United Kingdom': 'united-kingdom',
    'UK': 'united-kingdom',
    'Russian Federation': 'russia',
    'Korea, Republic of': 'south-korea',
    'South Korea': 'south-korea',
    'Korea, Democratic People\'s Republic of': 'north-korea',
    'North Korea': 'north-korea',
    'Democratic Republic of the Congo': 'democratic-republic-congo',
    'Republic of the Congo': 'republic-congo',
    'Central African Republic': 'central-african-republic',
    'Dominican Republic': 'dominican-republic',
    'Bosnia and Herzegovina': 'bosnia-herzegovina',
    'Bosnia and Herz.': 'bosnia-herzegovina',
    'Trinidad and Tobago': 'trinidad-tobago',
    'Saint Vincent and the Grenadines': 'saint-vincent-grenadines',
    'São Tomé and Príncipe': 'sao-tome-principe',
    'Antigua and Barbuda': 'antigua-barbuda',
    'Czech Republic': 'czech-republic',
    'Czechia': 'czech-republic'
  };
  
  // Check for special cases first
  if (specialCases[countryName]) {
    return specialCases[countryName];
  }
  
  // Standard slug creation
  return createSlug(countryName);
};

/**
 * Generate meta tags for a country page
 * @param {Object} country - Country data object
 * @returns {Object} - Meta tags object
 */
export const generateCountryMeta = (country) => {
  if (!country) return {};
  
  const title = `${country.name} - Trade Relations & News | Trade Relations Atlas`;
  const description = country.description 
    ? `Explore ${country.name}'s trade relations, diplomatic connections, and latest news. ${country.description.substring(0, 120)}...`
    : `Stay updated on ${country.name}'s international relations, trade partnerships, and global news coverage through our comprehensive analysis platform.`;
  
  return {
    title,
    description,
    keywords: `${country.name}, trade relations, international relations, diplomacy, ${country.continent || ''}, global news, economic analysis`,
    ogTitle: title,
    ogDescription: description,
    ogType: 'article',
    ogUrl: `https://www.traderelationsatlas.com/countries/${createCountrySlug(country.name)}`,
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description
  };
};

/**
 * Generate meta tags for the world map page
 * @returns {Object} - Meta tags object
 */
export const generateWorldMapMeta = () => {
  const title = 'Interactive World Map - Trade Relations Atlas';
  const description = 'Explore global trade relations through our interactive world map. Select countries to view their diplomatic connections, trade partnerships, and latest news coverage.';
  
  return {
    title,
    description,
    keywords: 'world map, interactive map, global trade, international relations, country selection, trade atlas, diplomacy visualization',
    ogTitle: title,
    ogDescription: description,
    ogType: 'website',
    ogUrl: 'https://www.traderelationsatlas.com/world-map',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description
  };
};

/**
 * Generate structured data for a country page
 * @param {Object} country - Country data object
 * @returns {Object} - JSON-LD structured data
 */
export const generateCountryStructuredData = (country) => {
  if (!country) return null;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    'name': country.name,
    'description': country.description || `Information about ${country.name}'s trade relations and international connections.`,
    'url': `https://www.traderelationsatlas.com/countries/${createCountrySlug(country.name)}`,
    'sameAs': country.wikipedia_url ? [country.wikipedia_url] : [],
    'geo': country.latitude && country.longitude ? {
      '@type': 'GeoCoordinates',
      'latitude': country.latitude,
      'longitude': country.longitude
    } : undefined,
    'containedInPlace': country.continent ? {
      '@type': 'Place',
      'name': country.continent
    } : undefined
  };
};

export default {
  createSlug,
  slugToTitle,
  createCountrySlug,
  generateCountryMeta,
  generateWorldMapMeta,
  generateCountryStructuredData
};
